import React, { useRef, useState } from 'react'
import { X, Wrench, Clock, AlertCircle, Loader2, Paperclip, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'

const MAX_FILES = 10
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.doc,.docx,.xls,.xlsx'

interface MarkExecutedModalProps {
  isOpen: boolean
  onClose: () => void
  onMarkExecuted: (executedAt: string, notes?: string, attachmentUrls?: string[]) => Promise<void>
  requestId: string
  equipmentDescription?: string
  problemDescription?: string
  estimatedCost?: number
  scheduledArrangementDate?: string
}

export default function MarkExecutedModal({
  isOpen,
  onClose,
  onMarkExecuted,
  requestId,
  equipmentDescription,
  problemDescription,
  estimatedCost,
  scheduledArrangementDate
}: MarkExecutedModalProps) {
  const [executedAt, setExecutedAt] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Upload states
  const [files, setFiles] = useState<File[]>([])
  const [uploadedAttachments, setUploadedAttachments] = useState<{ webViewLink: string; name?: string }[]>([])
  const [fileError, setFileError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Define data/hora máxima como agora
  const now = new Date()
  const maxDateTime = now.toISOString().slice(0, 16)

  const validateDate = (dateString: string): boolean => {
    if (!dateString) return false
    const selectedDate = new Date(dateString)
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    return selectedDate <= today
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null)
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return

    const totalCount = uploadedAttachments.length + files.length + selected.length
    if (totalCount > MAX_FILES) {
      setFileError(`Máximo de ${MAX_FILES} arquivos permitidos`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    const oversized = selected.find(f => f.size > MAX_FILE_SIZE)
    if (oversized) {
      setFileError(`O arquivo "${oversized.name}" excede o limite de 10 MB`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setFiles(prev => [...prev, ...selected])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
    setFileError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateDate(executedAt)) {
      setError('A data de execução não pode ser futura')
      return
    }

    if (notes && notes.length > 500) {
      setError('As notas devem ter no máximo 500 caracteres')
      return
    }

    let attachmentLinks: string[] = uploadedAttachments.map(a => a.webViewLink)

    try {
      setLoading(true)
      setError(null)

      // (a) Upload — só se houver files novos E ainda não subidos
      if (files.length > 0 && uploadedAttachments.length === 0) {
        const formData = new FormData()
        files.forEach(f => formData.append('files', f))
        formData.append('resourceType', 'maintenance_request')
        formData.append('resourceId', requestId)
        const result = await apiService.uploadFiles(formData)
        // Backend retorna { data: { files: [...] } }. Filtramos itens sem webViewLink
        // porque o link é obrigatório para abrir o anexo posteriormente.
        const rawAttachments = result.data?.files ?? []
        const newAttachments = rawAttachments
          .filter((a): a is { webViewLink: string; id?: string; originalName?: string; name?: string } => Boolean(a.webViewLink))
          .map(a => ({ webViewLink: a.webViewLink, name: a.originalName || a.name }))
        setUploadedAttachments(newAttachments)
        attachmentLinks = newAttachments.map(a => a.webViewLink)
      }

      // (b) Disparar transição de status — backend persiste attachmentUrls junto da transição
      await onMarkExecuted(
        executedAt,
        notes.trim() || undefined,
        attachmentLinks.length > 0 ? attachmentLinks : undefined
      )

      toast.success('Requisição marcada como executada com sucesso!', {
        duration: 3000,
        position: 'top-right',
      })

      // Reset full
      setExecutedAt('')
      setNotes('')
      setFiles([])
      setUploadedAttachments([])

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar como executado')
      // NÃO limpar uploadedAttachments — permite retry sem reupload
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setExecutedAt('')
      setNotes('')
      setError(null)
      setFiles([])
      setUploadedAttachments([])
      setFileError(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div data-testid="mark-executed-modal" className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-brand-primary-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                Marcar como Executado
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Informações da solicitação */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-1">
                {equipmentDescription || `Solicitação #${requestId?.substring(0, 8)}...`}
              </p>
              {problemDescription && (
                <p className="text-sm text-brand-text-secondary mb-2">
                  {problemDescription.length > 100
                    ? `${problemDescription.substring(0, 100)}...`
                    : problemDescription}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs text-brand-text-muted">
                {estimatedCost && (
                  <span>
                    Custo: <span className="font-medium text-brand-text-secondary">€{estimatedCost.toFixed(2)}</span>
                  </span>
                )}
                {scheduledArrangementDate && (
                  <span>
                    Previsto: <span className="font-medium text-brand-text-secondary">
                      {new Date(scheduledArrangementDate).toLocaleDateString('pt-PT')}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Data de execução (obrigatório) */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Data de Execução *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-text-muted" />
                  <input
                    type="datetime-local"
                    value={executedAt}
                    onChange={(e) => setExecutedAt(e.target.value)}
                    max={maxDateTime}
                    required
                    data-testid="executed-at-input"
                    className="w-full rounded-md border-brand-neutral-300 pl-10 shadow-sm focus:border-brand-primary-500 focus:ring-brand-primary-500"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-xs text-brand-text-muted">
                  Data e hora não podem ser futuras em relação à data/hora atual
                </p>
              </div>

              {/* Notas da execução (opcional) */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Notas da Execução (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Adicione notas sobre a execução da manutenção..."
                  className="w-full rounded-md border-brand-neutral-300 shadow-sm focus:border-brand-primary-500 focus:ring-brand-primary-500"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-brand-text-muted">
                  {notes.length}/500 caracteres
                </p>
              </div>

              {/* Anexos (opcional) */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Anexos (opcional)
                </label>

                {/* Arquivos já enviados (retry-safe) */}
                {uploadedAttachments.length > 0 && (
                  <ul className="mb-2 space-y-1">
                    {uploadedAttachments.map((att, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{att.name || `Arquivo ${i + 1}`}</span>
                        <span className="ml-auto whitespace-nowrap font-medium">já enviado</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Arquivos selecionados ainda não enviados */}
                {files.length > 0 && (
                  <ul className="mb-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-brand-text-secondary bg-brand-surface-50 border border-brand-neutral-200 rounded px-2 py-1">
                        <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-brand-text-muted" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-brand-text-muted ml-1 whitespace-nowrap">
                          ({(f.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          disabled={loading}
                          className="ml-auto text-red-400 hover:text-red-600 disabled:opacity-50"
                          aria-label="Remover arquivo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Botão de seleção */}
                {uploadedAttachments.length + files.length < MAX_FILES && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_EXTENSIONS}
                      onChange={handleFileChange}
                      disabled={loading}
                      className="hidden"
                      id="mark-executed-file-input"
                    />
                    <label
                      htmlFor="mark-executed-file-input"
                      className={`inline-flex items-center gap-2 cursor-pointer rounded-md border border-brand-neutral-300 bg-white px-3 py-1.5 text-sm text-brand-text-secondary hover:bg-brand-surface-50 ${loading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    >
                      <Paperclip className="h-4 w-4" />
                      Adicionar anexo
                    </label>
                    <p className="mt-1 text-xs text-brand-text-muted">
                      Até {MAX_FILES} arquivos, máx. 10 MB cada. PDF, imagens, vídeo (MP4, MOV), Word, Excel.
                    </p>
                  </div>
                )}

                {/* Erro de arquivo */}
                {fileError && (
                  <div className="flex items-center gap-2 mt-1 p-2 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                    <p className="text-xs text-red-700">{fileError}</p>
                  </div>
                )}
              </div>

              {/* Erro geral */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !executedAt || fileError !== null}
                  data-testid="mark-executed-submit-btn"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-primary-500 border border-transparent rounded-md hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {files.length > 0 && uploadedAttachments.length === 0 ? 'Enviando...' : 'Marcando...'}
                    </div>
                  ) : (
                    'Marcar como Executado'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
